/** Missing-leaf repair for existing municipal service objects only.
 * Provider values, meaningful false/zero, provenance and human decisions win. */
const SERVICE_FIELDS = ['provider', 'sourceDescription', 'avgMgd', 'peakMgd', 'permittedMgd', 'excessMgd', 'storageGallons', 'nearestMainMiles', 'lineToSite', 'connectionType', 'notes'] as const;
const SERVICES = ['municipalSupply', 'wastewaterService'] as const;
const missing = (value: unknown) => value == null || (typeof value === 'number' && Number.isNaN(value));
const record = (value: unknown): Record<string, unknown> | undefined => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
function pinned(service: typeof SERVICES[number], field: string, protectedFields: ReadonlySet<string>): boolean {
  const prefix = service === 'municipalSupply' ? 'supply' : 'wastewater';
  const schemaKey = `${prefix}${field[0]!.toUpperCase()}${field.slice(1)}`;
  return protectedFields.has(service) || protectedFields.has(`${service}.${field}`) || protectedFields.has(schemaKey)
    || (service === 'wastewaterService' && field === 'sourceDescription' && protectedFields.has('wastewaterPlantDescription'));
}
export function hasMissingWaterServiceLeaves(data: Record<string, unknown>, protectedFields: ReadonlySet<string> = new Set()): boolean {
  return SERVICES.some(service => {
    const current = record(data[service]);
    if (!current || record(current.provenance)?.claimId) return false;
    return SERVICE_FIELDS.some(field => !pinned(service, field, protectedFields) && missing(current[field]));
  });
}
/** Returns exact newly adopted leaf paths for the existing source-stamping contract. */
export function fillMissingWaterServiceLeaves(data: Record<string, unknown>, incoming: Record<string, unknown>, protectedFields: ReadonlySet<string> = new Set()): string[] {
  const filled: string[] = [];
  for (const service of SERVICES) {
    const current = record(data[service]); const candidate = record(incoming[service]);
    if (!current || !candidate || record(current.provenance)?.claimId
      || record(candidate.provenance)?.appliesToProject === 'other-project') continue;
    // Capacities cannot be combined into a different named provider's service.
    if (typeof current.provider === 'string' && current.provider.trim()
      && typeof candidate.provider === 'string' && candidate.provider.trim()
      && current.provider.trim() !== candidate.provider.trim()) continue;
    for (const field of SERVICE_FIELDS) {
      const value = candidate[field];
      if (pinned(service, field, protectedFields) || !missing(current[field]) || missing(value)) continue;
      // The maintained extractor owns validation. Do not admit metadata or composite extensions here.
      if (!['string', 'number', 'boolean'].includes(typeof value) || (typeof value === 'number' && !Number.isFinite(value))) continue;
      current[field] = value;
      filled.push(`${service}.${field}`);
    }
  }
  return filled;
}
